import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

/* ---------- tiny inline icons (kept dependency-free, matches Gallery.jsx) ---------- */
const IconChevronLeft = (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" {...props}>
        <polyline points="15 18 9 12 15 6" />
    </svg>
);
const IconChevronRight = (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" {...props}>
        <polyline points="9 18 15 12 9 6" />
    </svg>
);
const IconClose = (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" {...props}>
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);
const IconArrowLeft = (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" {...props}>
        <line x1="19" y1="12" x2="5" y2="12" />
        <polyline points="12 19 5 12 12 5" />
    </svg>
);
const IconPlay = (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
        <polygon points="6 3 20 12 6 21 6 3" />
    </svg>
);

const isVideo = (src) => typeof src === "string" && src.toLowerCase().endsWith(".mp4");

/* bento span pattern — repeats every 9 tiles */
const spanClass = (i) => {
    const pos = i % 9;
    if (pos === 0) return "col-span-2 row-span-2";
    if (pos === 4) return "row-span-2";
    if (pos === 7) return "col-span-2";
    return "";
};

export default function AlbertoGallery({ images, onBack }) {

    const [activeIndex, setActiveIndex] = useState(null);

    const openLightbox = (index) => setActiveIndex(index);
    const closeLightbox = () => setActiveIndex(null);

    const showPrev = useCallback(() => {
        setActiveIndex((i) => (i === null ? null : (i - 1 + images.length) % images.length));
    }, [images.length]);

    const showNext = useCallback(() => {
        setActiveIndex((i) => (i === null ? null : (i + 1) % images.length));
    }, [images.length]);

    useEffect(() => {
        if (activeIndex === null) return;
        const handleKey = (e) => {
            if (e.key === "Escape") closeLightbox();
            if (e.key === "ArrowLeft") showPrev();
            if (e.key === "ArrowRight") showNext();
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [activeIndex, showPrev, showNext]);

    return (
        <section className="min-h-screen bg-[#EFEDE3] text-[#12302E] py-10 md:py-14">

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,600;1,9..144,500&family=Inter:wght@400;500&family=IBM+Plex+Mono:wght@400;500&display=swap');
                .font-display { font-family: 'Fraunces', serif; }
                .font-body { font-family: 'Inter', sans-serif; }
                .font-mono { font-family: 'IBM Plex Mono', monospace; }
            `}</style>

            <div className="max-w-7xl mx-auto px-4 md:px-6">

                {/* ---------- HEADER ---------- */}
                <div className="flex items-center justify-between mb-8">
                    <button
                        type="button"
                        onClick={onBack}
                        className="inline-flex items-center gap-2 font-body text-sm text-[#12302E]/70 hover:text-[#12302E] transition group"
                    >
                        <span className="w-8 h-8 rounded-full border border-[#12302E]/20 flex items-center justify-center group-hover:border-[#12302E]/40 group-hover:-translate-x-0.5 transition">
                            <IconArrowLeft className="w-4 h-4" />
                        </span>
                        Back to gallery
                    </button>

                    <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#C98A3E]">
                        {String(images.length).padStart(2, "0")} moments
                    </span>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="mb-10"
                >
                    <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#C98A3E]">
                        Featured chapter
                    </span>
                    <h1 className="font-display italic text-4xl md:text-5xl mt-2">
                        Alberto Family Tour in Sri Lanka
                    </h1>
                </motion.div>

                {/* ---------- GRID ---------- */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 auto-rows-[150px] md:auto-rows-[190px] gap-3 md:gap-4 grid-flow-dense">

                    {images.map((src, index) => (
                        <motion.button
                            key={index}
                            type="button"
                            onClick={() => openLightbox(index)}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.4, delay: (index % 9) * 0.04 }}
                            whileHover={{ scale: 0.98 }}
                            className={`group relative rounded-2xl overflow-hidden bg-[#12302E]/5 ${spanClass(index)}`}
                        >
                            {isVideo(src) ? (
                                <>
                                    <video
                                        src={src}
                                        className="w-full h-full object-cover"
                                        muted
                                        playsInline
                                        preload="metadata"
                                    />
                                    <div className="absolute inset-0 bg-black/25 flex items-center justify-center">
                                        <span className="w-11 h-11 rounded-full bg-white/90 flex items-center justify-center text-[#12302E] pl-0.5 group-hover:scale-110 transition">
                                            <IconPlay className="w-5 h-5" />
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <img
                                    src={src}
                                    alt={`Alberto family tour ${index + 1}`}
                                    className="w-full h-full object-cover transition duration-500 group-hover:scale-110 group-hover:brightness-90"
                                />
                            )}

                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition duration-300" />
                            <span className="absolute bottom-3 left-3 font-mono text-[10px] tracking-widest text-white opacity-0 group-hover:opacity-90 transition duration-300">
                                {String(index + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
                            </span>
                        </motion.button>
                    ))}

                </div>
            </div>

            {/* ---------- LIGHTBOX ---------- */}
            <AnimatePresence>
                {activeIndex !== null && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-[#0D2422]/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
                        onClick={closeLightbox}
                    >
                        <button
                            onClick={closeLightbox}
                            aria-label="Close"
                            className="absolute top-5 right-5 md:top-8 md:right-8 text-white/80 hover:text-white transition p-2 rounded-full hover:bg-white/10"
                        >
                            <IconClose className="w-6 h-6" />
                        </button>

                        <button
                            onClick={(e) => { e.stopPropagation(); showPrev(); }}
                            aria-label="Previous"
                            className="absolute left-3 md:left-8 text-white/80 hover:text-white transition p-2 md:p-3 rounded-full hover:bg-white/10"
                        >
                            <IconChevronLeft className="w-7 h-7 md:w-8 md:h-8" />
                        </button>

                        <motion.div
                            key={activeIndex}
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.98 }}
                            transition={{ duration: 0.25 }}
                            className="relative max-w-4xl w-full"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {isVideo(images[activeIndex]) ? (
                                <video
                                    src={images[activeIndex]}
                                    className="w-full max-h-[78vh] rounded-xl shadow-2xl"
                                    controls
                                    autoPlay
                                    playsInline
                                />
                            ) : (
                                <img
                                    src={images[activeIndex]}
                                    alt=""
                                    className="w-full max-h-[78vh] object-contain rounded-xl shadow-2xl"
                                />
                            )}
                            <p className="text-center font-mono text-xs tracking-[0.3em] text-white/50 mt-4">
                                {String(activeIndex + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
                            </p>
                        </motion.div>

                        <button
                            onClick={(e) => { e.stopPropagation(); showNext(); }}
                            aria-label="Next"
                            className="absolute right-3 md:right-8 text-white/80 hover:text-white transition p-2 md:p-3 rounded-full hover:bg-white/10"
                        >
                            <IconChevronRight className="w-7 h-7 md:w-8 md:h-8" />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

        </section>
    );
}
